import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHome,
  faWallet,
  faPiggyBank,
  faCalendarAlt,
  faChartLine,
  faList,
  faGift,
  faCog,
  faUsers,
  faExchangeAlt,
  faBullseye,
  faUser,
  faSignOutAlt,
  faMoon,
  faSun,
  faChevronDown,
  faChevronRight,
  faHistory,
  faTags,
  faLayerGroup,
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import axios from "axios";

export default function DesktopSidebar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [cachedImage, setCachedImage] = useState(null);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [totalBalance, setTotalBalance] = useState(null);
  const [totalSavingBalance, setTotalSavingBalance] = useState(null);

  // Cache user image
  useEffect(() => {
    if (!user?.picture) return;
    const key = `user_avatar_${user?.id || user?.email || "unknown"}`;
    const stored = localStorage.getItem(key);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed.dataUrl) setCachedImage(parsed.dataUrl);
      } catch (e) {}
    } else {
      fetch(user.picture)
        .then((r) => r.blob())
        .then((b) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            setCachedImage(reader.result);
            localStorage.setItem(key, JSON.stringify({ dataUrl: reader.result, ts: Date.now() }));
          };
          reader.readAsDataURL(b);
        })
        .catch(() => {});
    }
  }, [user?.picture, user?.id, user?.email]);

  // Fetch balances
  useEffect(() => {
    const fetchBalances = async () => {
      try {
        const [bResp, sResp] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/total-balance`),
          axios.get(`${process.env.REACT_APP_API_URL}/total-saving-balance`),
        ]);
        setTotalBalance(bResp?.data?.totalBalance);
        setTotalSavingBalance(sResp?.data?.totalSavingBalance);
      } catch (err) {
        console.error(err);
      }
    };
    fetchBalances();
  }, []);

  const toggleMenu = (menuId) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [menuId]: !prev[menuId],
    }));
  };

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const navItems = [
    { icon: faHome, label: t("home", "Головна"), path: "/" },
    {
      icon: faWallet,
      label: t("wallet", "Гаманець"),
      id: "wallet",
      children: [
        {
          icon: faWallet,
          label: t("balance", "Баланс"),
          path: "/balance",
          value: totalBalance != null ? `${Number(totalBalance).toFixed(0)}€` : null,
        },
        {
          icon: faPiggyBank,
          label: t("savings", "Накопичення"),
          path: "/savings",
          value: totalSavingBalance != null ? `${Number(totalSavingBalance).toFixed(0)}€` : null,
        },
        { icon: faCalendarAlt, label: t("monthlyPayments", "Щомісячні"), path: "/monthly-payments" },
      ],
    },
    {
      icon: faChartLine,
      label: t("dashboard", "Дашборд"),
      id: "dashboard",
      children: [
        { icon: faHistory, label: t("spendingHistory", "Історія витрат"), path: "/dashboard/spending-history" },
        { icon: faTags, label: t("spendingCategories", "Категорії витрат"), path: "/dashboard/spending-categories" },
      ],
    },
    { icon: faList, label: t("shoppingList", "Списки"), path: "/shopping-list" },
    { icon: faGift, label: t("wishList", "Віш Ліст"), path: "/wish-list" },
    {
      icon: faCog,
      label: t("settings", "Налаштування"),
      id: "settings",
      children: [
        { icon: faWallet, label: t("accountManagement", "Рахунки"), path: "/management/accounts" },
        { icon: faPiggyBank, label: t("savingAccountManagement", "Накопичувальні"), path: "/management/savings" },
        { icon: faBullseye, label: t("savingGoals", "Цілі"), path: "/management/saving-goals" },
        { icon: faExchangeAlt, label: t("exchangeRates", "Курси валют"), path: "/management/exchange-rates" },
        { icon: faUsers, label: t("allowedUsers", "Користувачі"), path: "/management/allowed-users" },
        { icon: faLayerGroup, label: t("mccCodes", "MCC Коди"), path: "/management/mcc-codes" },
      ],
    },
  ];

  return (
    <aside className="hidden lg:flex flex-col fixed left-0 top-0 h-screen w-[276px] bg-white dark:bg-dark-surface border-r border-gray-100 dark:border-gray-800 z-40 transition-colors">
      {/* Logo/Brand */}
      <div className="p-6 border-b border-gray-100 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <img src="/logo192.png" alt="Logo" className="w-10 h-10" />
          <h1 className="text-xl font-bold font-heading text-primary-600 dark:text-primary-400">
            {t("familyBudgeting", "Сімейний Бюджет")}
          </h1>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-4 space-y-1">
        {navItems.map((item, index) => (
          <div key={index}>
            {item.children ? (
              // Menu with submenu
              <div>
                <button
                  onClick={() => toggleMenu(item.id)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-left transition-all duration-200 ${
                    item.children.some((child) => isActive(child.path))
                      ? "bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300"
                      : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon={item.icon} className="w-5" />
                    <span className="font-medium">{item.label}</span>
                  </div>
                  <FontAwesomeIcon
                    icon={expandedMenus[item.id] ? faChevronDown : faChevronRight}
                    className="w-3 text-gray-400"
                  />
                </button>
                {expandedMenus[item.id] && (
                  <div className="mt-1 ml-4 space-y-1">
                    {item.children.map((child, childIndex) => (
                      <button
                        key={childIndex}
                        onClick={() => navigate(child.path)}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                          isActive(child.path)
                            ? "bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300"
                            : "text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
                        }`}
                      >
                        <div className="flex items-center gap-2.5">
                          <FontAwesomeIcon icon={child.icon} className="w-4 text-gray-400 dark:text-gray-500" />
                          <span>{child.label}</span>
                        </div>
                        {child.value && (
                          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">{child.value}</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              // Simple menu item
              <button
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all duration-200 ${
                  isActive(item.path)
                    ? "bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300"
                    : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
                }`}
              >
                <FontAwesomeIcon icon={item.icon} className="w-5" />
                <span className="font-medium">{item.label}</span>
              </button>
            )}
          </div>
        ))}
      </nav>

      {/* User section */}
      <div className="p-4 border-t border-gray-100 dark:border-gray-800">
        <div className="flex items-center gap-3 mb-4">
          {cachedImage || user?.picture ? (
            <img
              src={cachedImage || user?.picture}
              alt="User"
              className="w-10 h-10 rounded-full object-cover border-2 border-primary-100 dark:border-primary-800"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center text-primary-600 dark:text-primary-400">
              <FontAwesomeIcon icon={faUser} />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-medium text-gray-900 dark:text-white truncate">{user?.name || "User"}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user?.email}</p>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={toggleTheme}
            className="flex-1 py-2 px-3 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors flex items-center justify-center gap-2"
          >
            <FontAwesomeIcon icon={theme === "light" ? faMoon : faSun} />
          </button>
          <button
            onClick={() => {
              logout();
              navigate("/login");
            }}
            className="flex-1 py-2 px-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg text-sm font-medium hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors flex items-center justify-center gap-2"
          >
            <FontAwesomeIcon icon={faSignOutAlt} />
          </button>
        </div>
      </div>
    </aside>
  );
}

